import { injectable } from "inversify";
import { EventDispatcher } from "../../entities/EventDispatcher";
import type { IService } from "../../entities/Service";
import type { PointerPosition } from "./types";

const DRAG_THRESHOLD_PX = 3; // Minimum distance in pixels to consider as a drag

type PointerEventPayload = {
	absolutePointerPosition: { x: number; y: number };
	relativePointerPosition: { x: number; y: number };
};

@injectable()
class DomElementsService implements IService {
	readonly serviceName = "🏠 DomElementsService";

	public readonly onTap = new EventDispatcher<PointerPosition>("onTap");
	public readonly onDoubleTap = new EventDispatcher<PointerPosition>("onDoubleTap");

	public readonly onPointerDown = new EventDispatcher<PointerPosition>("onPointerDown");
	public readonly onPointerMove = new EventDispatcher<PointerEventPayload>("onPointerMove");
	public readonly onPointerUp = new EventDispatcher<PointerPosition>("onPointerUp");

	public readonly onDragStart = new EventDispatcher<PointerPosition>("onDragStart");
	public readonly onDragMove = new EventDispatcher<PointerPosition>("onDragMove");
	public readonly onDragEnd = new EventDispatcher<PointerPosition>("onDragEnd");

	public readonly onWheel = new EventDispatcher<PointerPosition>("onWheel");

	private isPointerDown = false;
	private isDragging = false;
	private pointerDownPosition: PointerPosition = { x: 0, y: 0 };

	constructor(public readonly containerElement: HTMLDivElement) {}

	public initialize(): void {
		this.bindEvents();
	}

	public dispose(): void {
		this.unbindEvents();
	}

	public bindEvents(): void {
		this.containerElement.addEventListener("click", this.handleOnPointerTap);
		this.containerElement.addEventListener("dblclick", this.handleOnPointerDoubleTap);
		this.containerElement.addEventListener("pointerdown", this.handleOnPointerDown);
		this.containerElement.addEventListener("pointermove", this.handleOnPointerMove);
		this.containerElement.addEventListener("pointerup", this.handleOnPointerUp);
		this.containerElement.addEventListener("wheel", this.handleOnWheel);
	}

	public unbindEvents(): void {
		this.containerElement.removeEventListener("click", this.handleOnPointerTap);
		this.containerElement.removeEventListener("dblclick", this.handleOnPointerDoubleTap);
		this.containerElement.removeEventListener("pointerdown", this.handleOnPointerDown);
		this.containerElement.removeEventListener("pointermove", this.handleOnPointerMove);
		this.containerElement.removeEventListener("pointerup", this.handleOnPointerUp);
		this.containerElement.removeEventListener("wheel", this.handleOnWheel);
	}

	/* DOM Event Handlers */
	private handleOnPointerTap = (event: MouseEvent): void => {
		if (event.detail > 1) {
			return;
		}

		if (this.isDragging) {
			return;
		}

		this.onTap.dispatch({ x: event.clientX, y: event.clientY });
	};

	private handleOnPointerDoubleTap = (event: MouseEvent): void => {
		this.onDoubleTap.dispatch({ x: event.clientX, y: event.clientY });
	};

	private handleOnPointerDown = (event: PointerEvent): void => {
		this.isPointerDown = true;
		this.isDragging = false;
		this.pointerDownPosition = { x: event.clientX, y: event.clientY };

		this.onPointerDown.dispatch({ x: event.clientX, y: event.clientY });
	};

	private handleOnPointerMove = (event: PointerEvent): void => {
		const absolutePointerPosition = { x: event.clientX, y: event.clientY };

		const rect = this.containerElement.getBoundingClientRect();
		const relativePointerPosition = {
			x: absolutePointerPosition.x - rect.left,
			y: absolutePointerPosition.y - rect.top,
		};

		this.onPointerMove.dispatch({ absolutePointerPosition, relativePointerPosition });

		if (this.isPointerDown === false) {
			return;
		}

		if (!this.isDragging) {
			const deltaX = relativePointerPosition.x - this.pointerDownPosition.x;
			const deltaY = relativePointerPosition.y - this.pointerDownPosition.y;

			if (Math.abs(deltaX) < DRAG_THRESHOLD_PX && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
				return;
			}

			this.isDragging = true;
			this.onDragStart.dispatch(this.pointerDownPosition);
		}

		this.onDragMove.dispatch(relativePointerPosition);
	};

	private handleOnPointerUp = (event: PointerEvent): void => {
		const position = { x: event.clientX, y: event.clientY };

		if (this.isDragging) {
			this.onDragEnd.dispatch(position);
		}

		this.isPointerDown = false;
		// isDragging stays true until next pointerDown — so click handler can detect drag

		this.onPointerUp.dispatch(position);
	};

	private handleOnWheel = (event: WheelEvent): void => {
		this.onWheel.dispatch({ x: event.clientX, y: event.clientY });
	};
	/* *** */
}

export { DomElementsService, type PointerEventPayload };
